import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { Service } from "~/providers/service-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import ServiceForm from "~/components/service/service-form";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useService } from "~/providers/service-provider";

export const Route = createFileRoute(
  "/_main/settings/_layout/bookings/services",
)({
  component: Services,
});

function Services() {
  const { t } = useTranslation("common");
  const { services } = useService();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | undefined>();
  const handleDialog = (open: boolean) => {
    if (open === false) {
      setSelectedService(undefined);
    }
    setOpenDialog(open);
  };
  const columns: ColumnDef<Service>[] = [
    {
      id: t("Services.image"),
      accessorKey: "startDate",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Services.image")} />
      ),
      cell: ({ row: { original: service }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <div className="max-w-[50px] max-h-[38px] relative">
            <div
              className="bg-cover bg-center w-full h-full p-4 bg-no-repeat"
              style={{
                backgroundImage: `url(/images/placeholder.png)`,
              }}
            ></div>
          </div>
        );
      },
    },
    {
      id: t("Services.name"),
      accessorKey: "startDate",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Services.name")} />
      ),
      cell: ({ row: { original: service }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return <p>{service.name}</p>;
      },
    },
  ];
  return (
    <div>
      <SettingsHeader
        title={t("Services.page_title")}
        buttonLabel={t("Services.new_service")}
        buttonAction={() => {
          handleDialog(true);
        }}
      />
      <DataTable
        data={services}
        columns={columns}
        onSelect={(service) => {
          handleDialog(true);
          setSelectedService(service);
        }}
      />
      <Dialog open={openDialog} onOpenChange={handleDialog}>
        <DialogContent className="max-w-xl w-full">
          <DialogHeader>
            <DialogTitle>
              {selectedService
                ? selectedService.name
                : t("Services.new_service")}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto small-scrollbar p-4">
            <ServiceForm service={selectedService} />
          </div>
          <DialogFooter>
            <Button type="submit" form="service-form" variant="success">
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
